import { useCallback, useEffect, useRef } from 'react';

/**
 * A piece played ungraded writes its progress row (when it was last played, at
 * what tempo; see repertoireProgress) when playback stops on the piece page.
 * A player who leaves while the piece is still playing (Back, or another
 * section) never stops it there, so that write would be lost and the piece
 * would not show as recently played.
 *
 * This hook remembers that ungraded play ran since the last write, and makes
 * the write when the piece page unmounts with it still owed. The stop write
 * marks it done (the returned function), so a piece paused and then left is
 * written once.
 *
 * `playingUngraded`: the piece is playing, ungraded, in a mode that records
 * progress (not listen-only). `writeProgress`: the same ungraded write the
 * stop makes; read at unmount, so it sees the latest piece and tempo.
 */
export function useUngradedProgressOnLeave(
  playingUngraded: boolean,
  writeProgress: () => void,
): () => void {
  const owedRef = useRef(false);
  const writeRef = useRef(writeProgress);
  writeRef.current = writeProgress;

  useEffect(() => {
    if (playingUngraded) owedRef.current = true;
  }, [playingUngraded]);

  useEffect(
    () => () => {
      if (!owedRef.current) return;
      owedRef.current = false;
      writeRef.current();
    },
    [],
  );

  return useCallback(() => {
    owedRef.current = false;
  }, []);
}
